import { Box, Button, CircularProgress, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import * as React from "react";
import { PasswordInput, TextInput } from "react-admin";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import sendRequest from "../dataProvider/sendRequest";
import formatPhone from "./inputFormat/formatPhone";
import useNotify from "./useNotify";

interface FormValues {
  old_password: string;
  password: string;
  confirm_password: string;
  mobile_number?: string;
}

interface MenuItem {
  id: string;
  label: string;
  children: MenuItem[];
}

const MENU_ITEMS: MenuItem[] = [
  {
    id: "linkto_profile",
    label: "Profile",
    children: [],
  },
  {
    id: "linkto_contact",
    label: "Mobile",
    children: [],
  },
];

const Profile: React.FC = () => {
  const [selectedItem, setSelectedItem] = React.useState("linkto_profile");
  const [loading, setLoading] = React.useState(false);
  const { success, error } = useNotify();
  const navigate = useNavigate();
  const methods = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      old_password: "",
      password: "",
      confirm_password: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const password = watch("password");

  const handleFormSubmit = async (values: FormValues) => {
    if (loading) return;

    setLoading(true);
    try {
      const reqUrl = "update-profile";
      let formJson: { [key: string]: any } = {
        option: selectedItem === "linkto_profile" ? "profile" : "contact",
      };
      if (selectedItem === "linkto_profile") {
        formJson.old_password = values.old_password;
        formJson.password = values.password;
        formJson.confirm_password = values.confirm_password;
      }
      if (selectedItem === "linkto_contact") {
        formJson.mobile_number = values.mobile_number;
      }
      const response = await sendRequest(reqUrl, {
        id: 0,
        data: formJson,
      });
      if (response && response.code === 200) {
        if (selectedItem === "linkto_profile") {
          success("Password updated successfully! Please login again.");
          //退出，重新登录
          navigate("/login");
        } else if (selectedItem === "linkto_contact") {
          success("Mobile number updated successfully!");
        }
        setLoading(false);
      } else {
        error(response?.message || "Failed to update");
        setLoading(false);
      }
    } catch (err) {
      error("An error occurred while updating profile");
      setLoading(false);
    }
  };

  return (
    <Grid container columnSpacing={1} sx={{ mb: 0 }}>
      <Grid item sx={{ width: 300, flexShrink: 0, mb: 0 }}>
        <Box
          sx={{
            display: "block",
            border: "1px solid #e0e0e3",
            // borderRadius: "10px",
            padding: "8px",
            paddingTop: "20px",
            backgroundColor: "#fff",
            height: "calc(100vh - 100px)",
          }}
          id="profile-menu-box"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {MENU_ITEMS.map((item) => (
              <Button
                key={item.id}
                onClick={() => setSelectedItem(item.id)}
                fullWidth
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  borderRadius: "0px",
                  fontSize: "1rem",
                  fontWeight: "normal",
                  fontFamily: "Proxima Nova",
                  padding: "5px 0.2em 5px 10px",
                  background:
                    selectedItem === item.id ? "#1976d233" : "inherit",
                  color: selectedItem === item.id ? "#000" : "grey",
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Grid>

      <Grid
        item
        xs
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          "& .MuiGrid-item": { fontSize: "14px" },
          scrollMarginTop: "0px",
        }}
      >
        <Box
          sx={{
            display: "block",
            border: "1px solid #e0e0e3",
            mt: "0px",
            // borderRadius: "10px",
            padding: "8px",
            paddingTop: "0px",
            backgroundColor: "#fff",
            height: "calc(100vh - 100px)",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "600px", margin: "auto", mt: 0 }}>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(handleFormSubmit)}
                noValidate
              >
                {/* hidden input selectedItem */}
                <input type="hidden" name="selectedItem" value={selectedItem} />
                {selectedItem === "linkto_profile" && (
                  <>
                    <Grid container spacing={2} sx={{ padding: "20px", mt: 0 }}>
                      <Grid item xs={12}>
                        <Typography>
                          According to compliance requirements at your
                          organization, your password has to be refreshed every
                          6 months.
                        </Typography>
                        <Typography>
                          When choosing a new password, please use one that does
                          not resemble the current one.
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      spacing={2}
                      sx={{ padding: "20px" }}
                      id="profile_edit_password_section"
                    >
                      <Grid item xs={12}>
                        <Controller
                          name="old_password"
                          control={control}
                          rules={{ required: "Old password is required" }}
                          render={({ field }) => (
                            <PasswordInput
                              source="old_password"
                              {...field}
                              variant="outlined"
                              label="Old Password"
                              fullWidth
                              error={!!errors.old_password}
                              helperText={errors.old_password?.message}
                            />
                          )}
                        />

                        <Controller
                          name="password"
                          control={control}
                          rules={{
                            required: "Password is required",
                            minLength: {
                              value: 8,
                              message: "Password must be at least 8 characters",
                            },
                            pattern: {
                              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                              message:
                                "Must include uppercase, lowercase and number",
                            },
                          }}
                          render={({ field }) => (
                            <PasswordInput
                              source="password"
                              {...field}
                              variant="outlined"
                              label="New Password"
                              fullWidth
                              error={!!errors.password}
                              helperText={errors.password?.message}
                            />
                          )}
                        />

                        <Controller
                          name="confirm_password"
                          control={control}
                          rules={{
                            required: "Please confirm your password",
                            validate: (value) =>
                              value === password ||
                              "The passwords do not match",
                          }}
                          render={({ field }) => (
                            <PasswordInput
                              source="confirm_password"
                              {...field}
                              variant="outlined"
                              label="Confirm Password"
                              fullWidth
                              error={!!errors.confirm_password}
                              helperText={errors.confirm_password?.message}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {selectedItem === "linkto_contact" && (
                  <Grid
                    container
                    spacing={2}
                    sx={{ padding: "20px" }}
                    id="profile_edit_contact_section"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6" className="hm-text-title">
                        Mobile Number Settings
                      </Typography>
                      <Typography className="hm-text">
                        Update your mobile number for account recovery and
                        notifications.
                      </Typography>
                      {/* mobile input */}
                      <Controller
                        name="mobile_number"
                        control={control}
                        rules={{
                          required: "Mobile number is required",
                        }}
                        render={({ field }) => (
                          <TextInput
                            source="mobile_number"
                            {...field}
                            format={formatPhone}
                            variant="outlined"
                            label="Mobile Number"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    fullWidth
                    sx={{
                      mt: 2,
                      width: "120px",
                    }}
                    onClick={methods.handleSubmit(handleFormSubmit)}
                  >
                    {loading ? (
                      <CircularProgress size={25} thickness={2} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Box>
              </form>
            </FormProvider>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Profile;
